import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';

import { TooltipComponent } from './tooltip.component';
import {
  registerTooltipOwner,
  unregisterTooltipOwner,
} from './tooltip-owner.registry';
import { TooltipPlacement } from './tooltip-placement';

let nextTooltipId = 0;

@Directive({
  // Preserve the public ngx-shared selector while Reader consumers migrate in place.
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[mheTooltip]',
  exportAs: 'mheTooltip',
  host: {
    '(touchstart)': 'onTouchStart()',
    '(touchend)': 'onTouchEnd()',
    '(touchcancel)': 'onTouchEnd()',
    '(focusin)': 'onFocus()',
    '(focusout)': 'close()',
    '(mouseenter)': 'onMouseEnter($event)',
    '(mouseleave)': 'close()',
    '[attr.aria-label]': 'ariaLabel()',
  },
})
export class TooltipDirective implements OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly renderer = inject(Renderer2);
  private readonly tooltipId = `reader-ui-tooltip-${nextTooltipId++}`;
  private readonly boundKeyDown = this.onKeyDown.bind(this);
  private readonly disabledObserver = this.createDisabledMutationObserver();

  private overlayRef: OverlayRef | null = null;
  private tooltipRef: ComponentRef<TooltipComponent> | null = null;
  private isTouching = false;

  readonly mheTooltip = input<string | TemplateRef<unknown> | null>('');
  readonly placement = input<TooltipPlacement>('top');
  readonly disableDefaultActions = input(false);
  readonly disableTooltip = input(false);
  readonly panelClass = input('');
  readonly alternateAriaLabel = input('');

  readonly shown = output<string>();
  readonly hidden = output<string>();
  readonly toggleShown = output<string>();

  protected readonly ariaLabel = computed(() => {
    const message = this.mheTooltip();
    return this.alternateAriaLabel() || (typeof message === 'string' ? message : null);
  });

  constructor() {
    effect(() => {
      const message = this.mheTooltip();
      const placement = this.placement();

      if (this.tooltipRef) {
        this.setTooltipContent(this.tooltipRef, message);
        this.tooltipRef.setInput('placement', placement);
        this.updatePositionStrategy(placement);
      }
    });
  }

  ngOnDestroy(): void {
    this.close();
    this.disabledObserver.disconnect();
  }

  open(): void {
    const message = this.mheTooltip();
    if (
      this.isOpen()
      || this.disableTooltip()
      || this.isTouching
      || this.isHostDisabled()
      || (!message && typeof message !== 'object')
    ) {
      return;
    }

    this.overlayRef = this.overlay.create(this.getOverlayConfig());
    this.tooltipRef = this.overlayRef.attach(new ComponentPortal(TooltipComponent));
    this.tooltipRef.setInput('id', this.tooltipId);
    this.tooltipRef.setInput('placement', this.placement());
    this.setTooltipContent(this.tooltipRef, message);

    registerTooltipOwner(this.tooltipRef.location.nativeElement, this);
    this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-describedby', this.tooltipId);
    this.updatePositionStrategy(this.placement());
    this.tooltipRef.instance.afterViewInit.subscribe(() => this.overlayRef?.updatePosition());
    this.tooltipRef.changeDetectorRef.detectChanges();

    window.addEventListener('keydown', this.boundKeyDown);
    this.shown.emit('TooltipDirective');
    this.toggleShown.emit('TooltipDirective');
  }

  close(): void {
    if (!this.tooltipRef) {
      return;
    }

    unregisterTooltipOwner(this.tooltipRef.location.nativeElement);
    this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
    this.overlayRef?.dispose();
    this.tooltipRef = null;
    this.overlayRef = null;
    window.removeEventListener('keydown', this.boundKeyDown);
    this.hidden.emit('TooltipDirective');
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  isOpen(): boolean {
    return this.tooltipRef !== null;
  }

  getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: this.panelClass(),
    });
  }

  onTouchStart(): void {
    this.isTouching = true;
  }

  onTouchEnd(): void {
    this.isTouching = false;
  }

  onFocus(): void {
    this.toggle();
  }

  onMouseEnter(event: MouseEvent): void {
    if (!(event.target as HTMLButtonElement | null)?.disabled) {
      this.open();
    }
  }

  private setTooltipContent(
    componentRef: ComponentRef<TooltipComponent>,
    message: string | TemplateRef<unknown> | null,
  ): void {
    componentRef.setInput('tooltipMessage', typeof message === 'string' ? message : '');
    componentRef.setInput('templateRef', typeof message === 'string' ? null : message);
  }

  private updatePositionStrategy(placement: TooltipPlacement): void {
    if (!this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.getPosition(placement)])
      .withPush(false);

    this.overlayRef.updatePositionStrategy(positionStrategy);
  }

  private getPosition(placement: TooltipPlacement): ConnectedPosition {
    const offset = 8;

    switch (placement) {
      case 'left':
        return {
          originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -offset,
        };
      case 'right':
        return {
          originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offset,
        };
      case 'bottom':
        return {
          originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offset,
        };
      case 'top':
      default:
        return {
          originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offset,
        };
    }
  }

  private createDisabledMutationObserver(): MutationObserver {
    const observer = new MutationObserver(() => {
      if (this.isHostDisabled()) {
        this.close();
      }
    });
    observer.observe(this.elementRef.nativeElement, { attributeFilter: ['disabled'] });
    return observer;
  }

  private isHostDisabled(): boolean {
    return Boolean((this.elementRef.nativeElement as HTMLButtonElement).disabled);
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (!this.disableDefaultActions() && event.key === 'Escape') {
      this.close();
    }
  }
}